/**
 * Festival Suggestions — Admin Review Inbox
 *
 * Surfaces every employee-submitted festival suggestion in one place with
 * approve/reject buttons. Approved suggestions are pushed into the org-wide
 * Optional Holiday list (handled server-side).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';
import { Calendar, CheckCircle2, XCircle, Sparkles, Inbox, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';

const STATUS_STYLE = {
  pending:  'bg-amber-50 text-amber-700 border border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const formatDateTime = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return String(d); }
};

export default function FestivalSuggestionsAdmin() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reviewModal, setReviewModal] = useState({ open: false, suggestion: null, action: null });
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const r = await adminApi.get(`/api/admin/festival-suggestions${q}`);
      setSuggestions(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error('Failed to load festival suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const openReview = (suggestion, action) => {
    setReviewModal({ open: true, suggestion, action });
    setAdminNotes('');
  };

  const submitReview = async () => {
    if (!reviewModal.suggestion) return;
    setSubmitting(true);
    try {
      await adminApi.put(
        `/api/admin/festival-suggestions/${reviewModal.suggestion._id}/review`,
        { action: reviewModal.action, admin_notes: adminNotes.trim() }
      );
      const isApprove = reviewModal.action === 'approve';
      toast.success(isApprove
        ? `Approved — "${reviewModal.suggestion.festival_name}" added to the Optional Holiday list.`
        : `Suggestion rejected.`
      );
      setReviewModal({ open: false, suggestion: null, action: null });
      setAdminNotes('');
      fetchSuggestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const counts = suggestions.reduce(
    (acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; acc.total += 1; return acc; },
    { pending: 0, approved: 0, rejected: 0, total: 0 }
  );

  return (
    <div className="space-y-4" data-testid="festival-suggestions-admin">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-500" />
          <div>
            <h2 className="text-lg font-bold text-[#1a1a2e]">Festival Suggestions</h2>
            <p className="text-xs text-neutral-500">
              Employee-proposed festival days. Approve to add them to the org-wide Optional Holiday list.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="suggestions-status-filter"
            className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-200"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSuggestions}
            disabled={loading}
            className="border-neutral-200"
            data-testid="refresh-suggestions"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-xl p-3 bg-amber-50 ring-1 ring-amber-200">
          <div className="text-xs font-medium text-amber-700">Pending</div>
          <div className="text-2xl font-bold text-amber-700 mt-0.5">{counts.pending}</div>
        </div>
        <div className="rounded-xl p-3 bg-emerald-50 ring-1 ring-emerald-200">
          <div className="text-xs font-medium text-emerald-700">Approved</div>
          <div className="text-2xl font-bold text-emerald-700 mt-0.5">{counts.approved}</div>
        </div>
        <div className="rounded-xl p-3 bg-red-50 ring-1 ring-red-200">
          <div className="text-xs font-medium text-red-700">Rejected</div>
          <div className="text-2xl font-bold text-red-700 mt-0.5">{counts.rejected}</div>
        </div>
        <div className="rounded-xl p-3 bg-neutral-50 ring-1 ring-neutral-200">
          <div className="text-xs font-medium text-neutral-700">Total shown</div>
          <div className="text-2xl font-bold text-neutral-700 mt-0.5">{counts.total}</div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="p-12 text-center text-neutral-500" data-testid="suggestions-loading">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : suggestions.length === 0 ? (
        <div className="p-12 text-center text-neutral-500 bg-white rounded-xl border border-neutral-200" data-testid="suggestions-empty">
          <Inbox className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
          <p className="text-sm">No suggestions match this filter.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600">Employee</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600">Date</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600">Festival</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600">Reason</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600">Submitted</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-neutral-600">Status</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {suggestions.map((s) => (
                <tr key={s._id} className="hover:bg-neutral-50/60" data-testid={`suggestion-row-${s._id}`}>
                  <td className="px-4 py-3 font-medium text-[#1a1a2e]">{s.employee_name || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-neutral-800">
                      <Calendar className="w-3.5 h-3.5 text-neutral-400" /> {formatDate(s.date)}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-[#1a1a2e]">{s.festival_name}</td>
                  <td className="px-4 py-3 text-neutral-600 max-w-xs truncate" title={s.reason || ''}>
                    {s.reason || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{formatDateTime(s.created_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={STATUS_STYLE[s.status] || 'bg-neutral-100 text-neutral-700'}>
                      {s.status}
                    </Badge>
                    {s.status !== 'pending' && s.admin_notes && (
                      <div className="text-xs text-neutral-400 mt-1 italic" title="Admin notes">
                        &quot;{s.admin_notes}&quot;
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.status === 'pending' ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => openReview(s, 'approve')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                          data-testid={`approve-${s._id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReview(s, 'reject')}
                          className="border-red-200 text-red-700 hover:bg-red-50 h-7 text-xs"
                          data-testid={`reject-${s._id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">
                        {s.reviewed_at ? `by ${s.reviewed_by?.slice?.(0, 8) || ''}…` : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== Review confirmation modal ===== */}
      <Dialog
        open={reviewModal.open}
        onOpenChange={(open) => !open && setReviewModal({ open: false, suggestion: null, action: null })}
      >
        <DialogContent className="max-w-md bg-white" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {reviewModal.action === 'approve' ? 'Approve Festival' : 'Reject Festival'}
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-600">
              {reviewModal.action === 'approve'
                ? 'This festival will be added to the org-wide Optional Holiday list and become selectable by all employees (subject to the 12-day pre-selection deadline).'
                : 'This suggestion will be marked rejected. The employee will see your note.'}
            </DialogDescription>
          </DialogHeader>
          {reviewModal.suggestion && (
            <div className="bg-neutral-50 rounded-lg p-3 space-y-1 text-sm">
              <div><span className="text-neutral-500">Festival:</span> <span className="font-medium">{reviewModal.suggestion.festival_name}</span></div>
              <div><span className="text-neutral-500">Date:</span> {formatDate(reviewModal.suggestion.date)}</div>
              <div><span className="text-neutral-500">Suggested by:</span> {reviewModal.suggestion.employee_name}</div>
              {reviewModal.suggestion.reason && (
                <div><span className="text-neutral-500">Reason:</span> {reviewModal.suggestion.reason}</div>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">
              Admin notes {reviewModal.action === 'reject' && <span className="text-red-500">(recommended)</span>}
            </label>
            <textarea
              rows={3}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              maxLength={500}
              placeholder={reviewModal.action === 'approve'
                ? 'Optional context for the employee…'
                : 'Why is this being rejected?'}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              data-testid="admin-notes-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewModal({ open: false, suggestion: null, action: null })}
              className="border-neutral-200"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={submitting}
              className={reviewModal.action === 'approve'
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-review"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {reviewModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
